import { useState, useMemo } from 'react';
import { Document, Page } from 'react-pdf';
import { Loader2, FileText } from 'lucide-react';
import { PDFContentProps } from './types';

// Worker already configured in src/lib/pdfConfig.ts (imported in main.tsx)

// High-DPI rendering: calculate quality multiplier for sharp text on zoom
// Capped at 2.5 to avoid memory crashes on older devices
const getQualityMultiplier = () => {
  if (typeof window === 'undefined') return 2;
  const dpr = window.devicePixelRatio || 1;
  return Math.min(Math.max(dpr, 2), 2.5);
};

export function PDFContent({
  url,
  fileData,
  rotation,
  onLoadSuccess,
  onLoadError,
  containerWidth,
}: PDFContentProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set());

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    onLoadSuccess(numPages);
  };

  const handleDocumentLoadError = (error: Error) => {
    console.error('[PDFContent] Load error:', error.message, error);
    onLoadError(`Impossible de charger le PDF: ${error.message}`);
  };

  const handlePageLoadSuccess = (pageNumber: number) => {
    setLoadedPages(prev => new Set(prev).add(pageNumber));
  };

  // Quality multiplier for high-DPI rendering (2x to 2.5x)
  const qualityMultiplier = useMemo(() => getQualityMultiplier(), []);

  // Calculate page width for fit-width behavior (visual size on screen)
  // Leave some padding (64px total)
  const pageWidth = useMemo(() => {
    const maxWidth = containerWidth - 64;
    return Math.max(300, Math.min(maxWidth, 1200));
  }, [containerWidth]);

  // Render width is larger for high-DPI sharpness
  const renderWidth = useMemo(() => {
    return pageWidth * qualityMultiplier;
  }, [pageWidth, qualityMultiplier]);

  // Generate array of page numbers
  const pages = useMemo(() => {
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }, [numPages]);

  return (
    <Document
      file={fileData || url}
      onLoadSuccess={handleDocumentLoadSuccess}
      onLoadError={handleDocumentLoadError}
      loading={
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Chargement du PDF...</p>
        </div>
      }
      error={
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <FileText className="w-12 h-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Erreur de chargement du PDF</p>
        </div>
      }
      className="flex flex-col items-center gap-4 py-4"
    >
      {pages.map((pageNumber) => (
        <div 
          key={pageNumber} 
          className="relative shadow-lg rounded-lg overflow-hidden bg-white"
          style={{ 
            width: pageWidth,
            transform: `rotate(${rotation}deg)`,
          }}
        >
          {/* 
            High-DPI Rendering Strategy:
            - Canvas is rendered at renderWidth (2-2.5x larger for sharpness)
            - CSS scales it back to pageWidth for correct visual size
            - Result: crisp text even when zoomed in
          */}
          <div 
            style={{ 
              width: pageWidth,
              height: (pageWidth * 1.4), // Approximate A4 aspect ratio
              overflow: 'hidden',
            }}
          >
            {/* Inner container applies the scale-down transform */}
            <div
              style={{
                transform: `scale(${1 / qualityMultiplier})`,
                transformOrigin: 'top left',
                width: renderWidth,
              }}
            >
              <Page
                pageNumber={pageNumber}
                width={renderWidth}
                onLoadSuccess={() => handlePageLoadSuccess(pageNumber)}
                loading={
                  <div 
                    className="flex items-center justify-center bg-secondary/30"
                    style={{ width: pageWidth, height: pageWidth * 1.4 }}
                  >
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                }
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </div>
          </div>
          {/* Page number indicator */}
          <div className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-md text-muted-foreground">
            {pageNumber} / {numPages}
          </div>

        </div>
      ))}
    </Document>
  );
}
